export type AnalyticsEvent =
  | "game_start"
  | "guess_submit"
  | "game_win"
  | "game_loss"
  | "new_game"
  | "share_result"
  | "ad_click"
  | "ad_dwell"
  | "scroll_depth";

type Payload = Record<string, string | number | boolean | undefined>;

const clarityId = import.meta.env.VITE_CLARITY_ID;
const clarityHosts: string[] = (
  import.meta.env.VITE_CLARITY_HOSTS || "wordleitaliano.vip,www.wordleitaliano.vip"
)
  .split(",")
  .map((host: string) => host.trim())
  .filter(Boolean);
const ga4Id = import.meta.env.VITE_GA4_ID;

export function initAnalytics() {
  const shouldLoadClarity =
    clarityId && clarityHosts.some((host: string) => window.location.hostname === host);

  if (shouldLoadClarity && !document.querySelector(`[data-clarity="${clarityId}"]`)) {
    const script = document.createElement("script");
    script.dataset.clarity = clarityId;
    script.textContent = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${clarityId}");`;
    document.head.appendChild(script);
  }

  if (ga4Id && !document.querySelector(`[data-ga4="${ga4Id}"]`)) {
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
    const script = document.createElement("script");
    script.async = true;
    script.dataset.ga4 = ga4Id;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
    document.head.appendChild(script);
    window.gtag("js", new Date());
    window.gtag("config", ga4Id, { anonymize_ip: true });
  }
}

export function trackEvent(event: AnalyticsEvent, payload: Payload = {}) {
  const enriched = {
    event,
    ...payload,
    path: window.location.pathname,
    ts: new Date().toISOString()
  };

  window.clarity?.("event", event);
  window.gtag?.("event", event, payload);

  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return;
  }

  window.navigator.sendBeacon?.(
    "/api/events",
    new Blob([JSON.stringify(enriched)], { type: "application/json" })
  ) ||
    fetch("/api/events", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(enriched),
      keepalive: true
    }).catch(() => undefined);
}

export function bindScrollDepthTracking() {
  const thresholds = [25, 50, 75, 100];
  const seen = new Set<number>();

  const onScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const height =
      document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const depth = height <= 0 ? 100 : Math.round((scrollTop / height) * 100);

    thresholds.forEach((threshold) => {
      if (depth >= threshold && !seen.has(threshold)) {
        seen.add(threshold);
        trackEvent("scroll_depth", { depth: threshold });
      }
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
  return () => window.removeEventListener("scroll", onScroll);
}
